import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, descargarArchivo } from '../../lib/api';
import { CAP_INFO, capInfo } from '../../lib/capitulos';
import { Ayuda } from '../../components/Ayuda';
import { Modal } from '../../components/Modal';
import { Spinner } from '../../components/Spinner';
import { useConfirm } from '../../components/ConfirmDialog';

interface Desactualizado { desde_version: number; hasta_version: number; campos: string[]; pendientes?: { codigo: string; motivo: string }[] }
interface Borrador { id: string; capitulo: string; version: number; agent_code: string; estado: 'generando' | 'listo' | 'error' | string; error?: string | null; created_at: string; ficha_version?: number | null; desactualizado?: Desactualizado | null }

export const AYUDA_CAPITULOS = 'Cada capítulo lo redacta un agente especializado a partir de la carpeta del proyecto (y de fuentes públicas cuando corresponde); todo dato lleva su fuente y lo que no existe queda como [FALTA]. Aquí ves las versiones, las previsualizas, las bajas a Word o las publicas en SharePoint.';

const fecha = (iso: string) => new Date(iso).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });

/** Pestaña Capítulos de un proyecto (2026-10-22): lista de borradores con sus acciones y "+ Redactar". */
export function CapitulosProyecto({ favoritoId }: { favoritoId: string }) {
  const confirmar = useConfirm();
  const navigate = useNavigate();
  const [aviso, setAviso] = useState<string | null>(null);
  const [pendienteTexto, setPendienteTexto] = useState<string | null>(null);
  const [borradores, setBorradores] = useState<Borrador[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redactar, setRedactar] = useState(false);
  const [capitulo, setCapitulo] = useState('cap1');
  const [instrucciones, setInstrucciones] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [ocupado, setOcupado] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    try {
      const d = await apiFetch<{ borradores: Borrador[] }>(`/api/dia/${favoritoId}/capitulos`);
      setBorradores(d.borradores);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar los capítulos.');
    } finally {
      setCargando(false);
    }
  }, [favoritoId]);
  useEffect(() => { void cargar(); }, [cargar]);
  useEffect(() => {
    if (!borradores.some((b) => b.estado === 'generando')) return;
    const t = window.setInterval(() => void cargar(), 5000);
    return () => window.clearInterval(t);
  }, [borradores, cargar]);
  const generando = borradores.some((b) => b.estado === 'generando');

  async function generar() {
    setEnviando(true);
    try {
      await apiFetch(`/api/dia/${favoritoId}/capitulos`, { method: 'POST', body: JSON.stringify({ capitulo, instrucciones: instrucciones.trim() || undefined }) });
      setRedactar(false);
      setInstrucciones('');
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo iniciar la redacción.');
    } finally {
      setEnviando(false);
    }
  }
  async function publicar(b: Borrador) {
    setOcupado(b.id);
    try {
      const r = await apiFetch<{ ruta: string; webUrl: string | null }>(`/api/dia/capitulos/${b.id}/sharepoint`, { method: 'POST' });
      if (r.webUrl) window.open(r.webUrl, '_blank', 'noopener');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo publicar en SharePoint.');
    } finally {
      setOcupado(null);
    }
  }
  async function actualizarFicha(b: Borrador) {
    const d = b.desactualizado; if (!d) return;
    if (!(await confirmar({ titulo: 'Actualizar datos cambiados', mensaje: `Se reemplazarán solo los valores de la Ficha DIA que cambiaron (${d.campos.join(', ')}) en las frases que los citan. Queda una versión nueva; la actual no se toca.` }))) return;
    setOcupado(b.id);
    try {
      const r = await apiFetch<{ borrador: { version: number }; aplicados: string[]; pendientes: { codigo: string; motivo: string }[] }>(`/api/dia/capitulos/${b.id}/actualizar-ficha`, { method: 'POST' });
      await cargar();
      if (r.pendientes.length) {
        const texto = `En el ${capInfo(b.capitulo).titulo} actualiza según la Ficha DIA estos datos que no pude ubicar: ${r.pendientes.map((p) => p.codigo).join(', ')}`;
        setAviso(`Versión v${r.borrador.version} creada: ${r.aplicados.length} dato(s) actualizado(s). ${r.pendientes.length} no se ubicaron solos (${r.pendientes.map((p) => p.codigo).join(', ')}).`);
        setPendienteTexto(texto);
      } else {
        setAviso(`Versión v${r.borrador.version} creada con los ${r.aplicados.length} dato(s) actualizados.`);
        setPendienteTexto(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo actualizar.');
    } finally {
      setOcupado(null);
    }
  }
  async function eliminar(b: Borrador) {
    if (!(await confirmar({ titulo: 'Eliminar borrador', mensaje: `¿Eliminar ${capInfo(b.capitulo).titulo} v${b.version}? No se puede deshacer.`, destructivo: true }))) return;
    try { await apiFetch(`/api/dia/capitulos/${b.id}`, { method: 'DELETE' }); await cargar(); }
    catch (e) { setError(e instanceof Error ? e.message : 'No se pudo eliminar.'); }
  }

  return (
    <section className="tarjeta p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-medium text-azul-noche">
          Capítulos de la DIA <span className="font-normal text-gris">( {borradores.length} )</span>
          <Ayuda texto={AYUDA_CAPITULOS} />
        </h2>
        <Ayuda texto="Elige el capítulo y, si quieres, dale instrucciones al redactor (p. ej. qué carpeta usar). Tarda de 2 a 30 minutos según el capítulo; puedes seguir trabajando mientras tanto." toque={false}>
          <button type="button" onClick={() => setRedactar(true)} disabled={generando} className="btn-secundario !h-8 !px-3 !text-[13px] disabled:opacity-50">+ Redactar capítulo</button>
        </Ayuda>
      </div>
      {error && <p role="alert" className="alerta-error mt-3">{error}</p>}
      {aviso && (
        <p role="status" className="mt-3 rounded-xl bg-emerald-50/70 px-3 py-2 text-[12.5px] text-emerald-900 ring-1 ring-inset ring-emerald-200">
          ✓ {aviso}{pendienteTexto && <> <button type="button" onClick={() => navigate(`/agentes/dia/${favoritoId}?texto=${encodeURIComponent(pendienteTexto)}`)} className="font-medium underline">Pedir a Andrés que complete el resto →</button></>}
        </p>
      )}
      {cargando ? (
        <div className="flex justify-center py-8"><Spinner tamano="md" /></div>
      ) : borradores.length === 0 ? (
        <p className="mt-4 py-6 text-center text-sm font-light text-gris">Aún no hay capítulos. Usa "+ Redactar capítulo" o pídeselo a Andrés en el chat.</p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100">
          {borradores.map((b) => (
            <li key={b.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-medium text-azul-noche">{capInfo(b.capitulo).titulo}</p>
                <p className="text-[11px] font-light text-gris">v{b.version} · {capInfo(b.capitulo).agente} · {fecha(b.created_at)}{b.estado === 'error' && b.error ? ` · ${b.error}` : ''}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {b.desactualizado && (
                  <Ayuda texto={`La Ficha DIA pasó de v${b.desactualizado.desde_version} a v${b.desactualizado.hasta_version} después de este borrador. Cambió: ${b.desactualizado.campos.join(', ')}. "Actualizar" reemplaza solo esos valores en las frases que los citan y crea una versión nueva; nada se regenera.`}>
                    <span className="chip bg-red-50 text-red-700 ring-red-200">⚠ desactualizado · ficha v{b.desactualizado.desde_version}→v{b.desactualizado.hasta_version}</span>
                  </Ayuda>
                )}
                {b.desactualizado && b.estado === 'listo' && (
                  <button type="button" onClick={() => void actualizarFicha(b)} disabled={ocupado === b.id} className="btn-secundario !h-8 !px-2.5 !text-[12px] disabled:opacity-50">{ocupado === b.id ? 'Actualizando…' : 'Actualizar datos cambiados'}</button>
                )}
                {b.estado === 'generando' ? (
                  <span className="inline-flex items-center gap-1.5 text-[12px] text-azul-profundo"><Spinner tamano="xs" /> redactando…</span>
                ) : b.estado === 'error' ? (
                  <span className="chip bg-red-50 text-red-700 ring-red-200">error</span>
                ) : (
                  <span className="chip bg-emerald-50 text-emerald-700 ring-emerald-200">listo ✓</span>
                )}
                <a href={`/agentes/dia/${favoritoId}/capitulo/${b.capitulo}/preview`} target="_blank" rel="noopener noreferrer" className="btn-fantasma !h-8 !px-2.5 !text-[12px]" title="Previsualizar en otra pestaña (se actualiza en vivo)">Ver</a>
                <button type="button" onClick={() => void descargarArchivo(`/api/dia/capitulos/${b.id}/docx`, `${b.capitulo}_v${b.version}.docx`)} disabled={b.estado !== 'listo'} className="btn-fantasma !h-8 !px-2.5 !text-[12px] disabled:opacity-40">Word</button>
                <button type="button" onClick={() => void publicar(b)} disabled={b.estado !== 'listo' || ocupado === b.id} className="btn-fantasma !h-8 !px-2.5 !text-[12px] disabled:opacity-40" title="Publica el Word en la carpeta '99. Agentes AMBEC' del proyecto">SharePoint</button>
                <button type="button" onClick={() => void eliminar(b)} className="btn-fantasma !h-8 !px-2.5 !text-[12px] text-gris hover:text-red-600">Eliminar</button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {redactar && (
        <Modal titulo="Redactar un capítulo" onCerrar={() => setRedactar(false)} ayuda="El redactor del capítulo lee la carpeta del proyecto (y las fuentes públicas que correspondan) y entrega una rev_0 con cada dato citado. Lo que no encuentra queda como [FALTA] para que el equipo lo complete.">
          <label className="etiqueta" htmlFor="cap-sel">Capítulo</label>
          <select id="cap-sel" value={capitulo} onChange={(e) => setCapitulo(e.target.value)} className="campo mt-1">
            {Object.entries(CAP_INFO).map(([k, v]) => <option key={k} value={k}>{v.titulo} · {v.agente}</option>)}
          </select>
          <label className="etiqueta mt-4" htmlFor="cap-ins">Instrucciones para el redactor (opcional)</label>
          <textarea id="cap-ins" value={instrucciones} onChange={(e) => setInstrucciones(e.target.value)} rows={3} maxLength={2000} className="campo mt-1 !h-auto resize-y" placeholder='p. ej. "usa principalmente la carpeta Cap1_Material"' />
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" onClick={() => setRedactar(false)} className="btn-fantasma">Cancelar</button>
            <button type="button" onClick={() => void generar()} disabled={enviando} className="btn-primario disabled:opacity-50">{enviando ? 'Iniciando…' : 'Redactar'}</button>
          </div>
        </Modal>
      )}
    </section>
  );
}
